import React, { Suspense, lazy } from "react";
import { Redirect, Router, Switch } from "react-router-dom";

import { Container } from "semantic-ui-react";
import History from "./services/History";
import NavBar from "./components/NavBar";
import PrivateRoute from "./components/PrivateRoute";
import ScrollToTop from "./components/ScrollToTop";
import SimpleLoader from "./components/SimpleLoader";
import withAuthorizationRequired from "./components/WithAuthorizationRequired";

const AdminConsole = lazy(() => import("./features/AdminConsole/AdminConsoleContextProvider"));
const AdminReviewQueue = lazy(() => import("./features/AdminReview/AdminReviewQueue"));
const CarrierDetails = lazy(() => import("./features/CarrierDetails/CarrierDetails"));
const CarrierSummary = lazy(() => import("./features/CarrierSummary/CarrierSummary"));
const ClientDetails = lazy(() => import("./features/ClientDetails/ClientDetails"));
const CloselyHeldBusinesses = lazy(() => import("./features/CloselyHeldBusiness/CloselyHeldBusinesses"));
const CloselyHeldBusiness = lazy(() => import("./features/CloselyHeldBusiness/CloselyHeldBusinessDashboard"));
const ContactDetails = lazy(() => import("./features/ContactDetails/ContactDetails"));
const DepositDashboard = lazy(() => import("./features/DepositSlipQueue/DepositDashboard"));
const DeathClaim = lazy(() => import("./features/DeathClaim/DeathClaim"));
const DeathClaimDashboard = lazy(() => import("./features/DeathClaimQueue/DeathClaimDashboard"));
const DisbursementQueue = lazy(() => import("./features/DisbursementQueue/DisbursementQueue"));
const DocumentApproval = lazy(() => import("./features/DocumentApproval/DocumentApproval"));
const DocumentManagementPage = lazy(() => import("./features/DocumentManagement/DocumentManagementPage"));
const Notifications = lazy(() => import("./features/Notifications/Notifications"));
const OrganizationSummary = lazy(() => import("./features/OrganizationSummary/OrganizationSummary"));
const PolicyDetails = lazy(() => import("./features/PolicyDetails/PolicyDetails"));
const PolicyGridData = lazy(() => import("./features/PolicyGrid/PolicyGridData"));
const Portfolio = lazy(() => import("./features/Portfolio/Portfolio"));
const Report = lazy(() => import("./features/Report/ReportContextProvider"));
const ReportBuilder = lazy(() => import("./features/ReportBuilder/ReportBuilder"));
const Search = lazy(() => import("./features/Search/Search"));
const TrustOriginationDashboard = lazy(() => import("./features/TrustOriginationQueue/TrustOriginationDashboard"));
const StandardUpgrade = lazy(() => import("./features/StandardUpgrade/StandardUpgrade"));
const TaxQueueDashboard = lazy(() => import("./features/TaxQueue/TaxQueueDashboard"));
const Trust = lazy(() => import("./features/TrustDetails/TrustContextProvider"));
const TrustGridData = lazy(() => import("./features/TrustGrid/TrustGridData"));
const TrustOnboard = lazy(() => import("./features/TrustOnboard/TrustOnboard"));
const TrustOriginationCaseDetails = lazy(() => import("./features/TrustOrigination/TrustOriginationDetails"));
const WorkflowTrackerQueue = lazy(() => import("./features/WorkflowTracker/WorkflowTrackerQueue"));
const WorkflowTrackerInstancePage = lazy(() => import("./features/WorkflowTracker/WorkflowTrackerInstancePage"));

const Calendar = lazy(() => import("./features/Calendar/Calendar"));
const Wills = lazy(() => import("./features/Will/Wills"));
const Will = lazy(() => import("./features/Will/Will"));
const WillDODUpdates = lazy(() => import("./features/Will/WillDODUpdates"));

const AppRoutes: React.FC = () => {
    return (
        <Router history={History}>
            <ScrollToTop />
            <NavBar />
            <Container style={{ marginTop: "5em", width: "auto" }}>
                <Suspense fallback={<SimpleLoader />}>
                    <Switch>
                        <PrivateRoute
                            path="/admin-console/:acSearch?"
                            component={withAuthorizationRequired(AdminConsole, {
                                roleRestrictedPage: "AdminConsole",
                                restrictedFeature: "Admin Console",
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/admin-review"
                            component={withAuthorizationRequired(AdminReviewQueue, {
                                restrictedFeature: "Admin Review",
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/carrier"
                            exact
                            component={withAuthorizationRequired(CarrierSummary, {
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/carrier/:CarrierID"
                            component={withAuthorizationRequired(CarrierDetails, {
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/closely-held-business"
                            exact
                            component={withAuthorizationRequired(CloselyHeldBusinesses, {
                                roleRestrictedPage: "CloselyHeldBusinesses",
                                productRestrictedPage: "Closely Held"
                            })}
                        />
                        <PrivateRoute
                            path="/closely-held-business/:closelyHeldBusinessID"
                            exact
                            component={withAuthorizationRequired(CloselyHeldBusiness, {
                                roleRestrictedPage: "CloselyHeldBusiness",
                                productRestrictedPage: "Closely Held"
                            })}
                        />
                        <PrivateRoute
                            path="/contact/:contactId"
                            component={withAuthorizationRequired(ContactDetails, {
                                productRestrictedPage: "Trust Services OR Will File"
                            })}
                        />
                        <PrivateRoute
                            path="/death-claim/:deathClaimID"
                            exact
                            component={withAuthorizationRequired(DeathClaim, {
                                restrictedFeature: "Death Claim",
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/death-claim-queue"
                            exact
                            component={withAuthorizationRequired(DeathClaimDashboard, {
                                restrictedFeature: "Death Claim",
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/deposits"
                            exact
                            component={withAuthorizationRequired(DepositDashboard, {
                                restrictedFeature: "Deposit Slip",
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/document-approval"
                            component={withAuthorizationRequired(DocumentApproval, {
                                restrictedFeature: "Document Approval",
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/document-management"
                            component={withAuthorizationRequired(DocumentManagementPage, {
                                productRestrictedPage: "Trust Services OR Will File"
                            })}
                        />
                        <PrivateRoute path="/notifications" component={Notifications} />
                        <PrivateRoute
                            path="/onboarding"
                            component={withAuthorizationRequired(TrustOnboard, {
                                roleRestrictedPage: "TrustOnboard",
                                restrictedFeature: "Trust Onboard",
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/organization"
                            component={withAuthorizationRequired(OrganizationSummary, {
                                roleRestrictedPage: "Organization",
                                productRestrictedPage: "Trust Services OR Will File"
                            })}
                        />
                        <PrivateRoute
                            path="/policy/:policyId"
                            component={withAuthorizationRequired(PolicyDetails, {
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/policygrid"
                            component={withAuthorizationRequired(PolicyGridData, {
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/portfolio"
                            exact
                            component={withAuthorizationRequired(Portfolio, { productRestrictedPage: "Trust Services" })}
                        />
                        <PrivateRoute
                            path="/portfolio/:clientId"
                            component={withAuthorizationRequired(ClientDetails, {
                                roleRestrictedPage: "Portfolio",
                                productRestrictedPage: "Has One Product"
                            })}
                        />
                        <PrivateRoute
                            path="/reports"
                            component={withAuthorizationRequired(Report, {
                                roleRestrictedPage: "Reports",
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/report-builder"
                            component={withAuthorizationRequired(ReportBuilder, {
                                roleRestrictedPage: "Reports",
                                productRestrictedPage: "Will File"
                            })}
                        />
                        <PrivateRoute
                            path="/search"
                            component={withAuthorizationRequired(Search, {
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/standardUpgrade"
                            component={withAuthorizationRequired(StandardUpgrade, {
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/trust-origination-queue"
                            component={withAuthorizationRequired(TrustOriginationDashboard, {
                                restrictedFeature: "Trust Origination",
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/trust/:trustId"
                            component={withAuthorizationRequired(Trust, {
                                productRestrictedPage: "Closely Held OR Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/trustgrid"
                            component={withAuthorizationRequired(TrustGridData, {
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/trustoriginationcase/:trustoriginatorcaseId"
                            component={withAuthorizationRequired(TrustOriginationCaseDetails, {
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/will/:willId"
                            component={withAuthorizationRequired(Will, {
                                productRestrictedPage: "Will File"
                            })}
                        />
                        <PrivateRoute
                            path="/will"
                            component={withAuthorizationRequired(Wills, {
                                productRestrictedPage: "Will File"
                            })}
                        />
                        <PrivateRoute
                            path="/will-dod-updates"
                            component={withAuthorizationRequired(WillDODUpdates, {
                                productRestrictedPage: "Will File"
                            })}
                        />
                        <PrivateRoute path="/workflow-tracker" exact component={WorkflowTrackerQueue} />
                        <PrivateRoute path="/workflow-tracker/:id" exact component={WorkflowTrackerInstancePage} />
                        <PrivateRoute
                            path="/tax-queue"
                            exact
                            component={withAuthorizationRequired(TaxQueueDashboard, { restrictedFeature: "Tax Return" })}
                        />
                        <PrivateRoute
                            path="/disbursement-queue"
                            exact
                            component={withAuthorizationRequired(DisbursementQueue, {
                                restrictedFeature: "Disbursement Queue",
                                productRestrictedPage: "Trust Services"
                            })}
                        />
                        <PrivateRoute
                            path="/calendar"
                            exact
                            component={withAuthorizationRequired(Calendar, { restrictedFeature: "Calendar" })}
                        />

                        <Redirect to="/portfolio" />
                    </Switch>
                </Suspense>
            </Container>
        </Router>
    );
};

export default AppRoutes;
